import React from 'react';
import Head from 'next/head';
import Facebook from './Facebook';
import Twitter from './Twitter';
import { withTranslation } from 'next-i18next';
import { TFunction } from 'i18next';
import { useRouter } from 'next/router';

const ogImg = '/static/images/og-img.png';
const icon32Path = '/static/images/icon_32.png';

// eslint-disable-next-line @typescript-eslint/no-var-requires

const getAlternates = (path: string) => {
  const alternates = [
    { hreflang: 'x-default', href: `https://www.lingualbox.com${path}` },
    { hreflang: 'en', href: `https://www.lingualbox.com${path}` },
    { hreflang: 'ja', href: `https://ja.lingualbox.com${path}` },
    { hreflang: 'ru', href: `https://ru.lingualbox.com${path}` },
    { hreflang: 'pt-br', href: `https://pt.lingualbox.com${path}` },
    { hreflang: 'es', href: `https://es.lingualbox.com${path}` },
    { hreflang: 'zh-tw', href: `https://tw.lingualbox.com${path}` },
  ];
  return alternates.map((link) => (
    <link
      key={`alternates-${link.hreflang}`}
      rel="alternate"
      hrefLang={link.hreflang}
      href={link.href}
    />
  ));
};

const getSiteUrl = (language: string | undefined) => {
  switch (language) {
    case 'en':
      return 'https://www.lingualbox.com';
    case 'ja':
      return 'https://ja.lingualbox.com';
    case 'es':
      return 'https://es.lingualbox.com';
    case 'pt':
      return 'https://pt.lingualbox.com';
    case 'tw':
      return 'https://tw.lingualbox.com';
    case 'ru':
      return 'https://ru.lingualbox.com';
    default:
      return 'https://www.lingualbox.com';
  }
};

const getOgLanguage = (language: string | undefined) => {
  switch (language) {
    case 'en':
      return 'en_US';
    case 'ja':
      return 'ja_JP';
    case 'es':
      return 'es_ES';
    case 'pt':
      return 'pt_BR';
    case 'tw':
      return 'zh_TW';
    case 'ru':
      return 'ru_RU';
    default:
      return 'en_US';
  }
};

// Complete tutorial: https://www.gatsbyjs.org/docs/add-seo-component/

interface SEOProps {
  t: TFunction;
  title?: string | null;
  desc?: string | null;
  banner?: string | null;
  pathname?: string | null;
  article?: boolean;
  node?: Record<string, unknown> | null;
}

const SEO: React.FC<SEOProps> = (props) => {
  const { title = null, desc = null, t, banner = null } = props;
  const defaultTitle = t('title.default');
  const defaultDescription = t('description.default');
  const headline = t('headline');
  const router = useRouter();
  const language = router.locale;
  const path = router.asPath;
  const siteUrl = getSiteUrl(language);
  const ogLanguage = getOgLanguage(language);
  const defaultBanner = ogImg;

  const seo = {
    title: title || defaultTitle,
    headline,
    description: desc || defaultDescription,
    image: `${siteUrl}${banner || defaultBanner}`,
    url: `${siteUrl}${path || ''}`,
    icon32Url: `${siteUrl}${icon32Path}`,
  };

  return (
    <>
      <Head>
        <meta
          key="meta-viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta
          key="meta-compatible"
          httpEquiv="X-UA-Compatible"
          content="IE=edge"
        />
        <meta
          key="content-type-text"
          httpEquiv="Content-Type"
          content="text/html; charset=utf-8"
        ></meta>
        <title>{seo.title}</title>
        <link rel="canonical" href={seo.url} />
        <meta key="meta-desc" name="description" content={seo.description} />
        <meta key="meta-image" name="image" content={seo.image} />
        {getAlternates(path)}
      </Head>
      <Facebook
        desc={seo.description}
        image={seo.image}
        title={seo.title}
        type={'website'}
        url={seo.url}
        locale={ogLanguage}
      />
      <Twitter title={seo.title} image={seo.image} desc={seo.description} />
    </>
  );
};

export default withTranslation('common')(SEO);
